import { SnackbarQueue } from "@rmwc/snackbar";
import "@rmwc/snackbar/styles";
import { ThemeProvider } from "@rmwc/theme";
import { Accueil } from "Accueil/Components/Accueil";
import { Achat } from "Achat/Components/Achat";
import { HistoAchats } from "Achat/Components/HistoAchats";
import { ErrorOverlay } from "App/ErrorOverlay/ErrorOverlay";
import { Header } from "App/Header/Components/Header";
import { Loading } from "App/Loading/Components/Loading";
import { Queue } from 'App/Toast/Toast';
import { APP_LOGIN_ISIPAY as loginIsipay, APP_MDP_ISIPAY as mdpIsipay } from "Config.json";
import { RESSOURCE_AUTHENTIFICATION, URL } from "Login/API/LoginAPI";
import { Account } from "Login/Components/Account";
import { Login } from "Login/Components/Login";
import moment from "moment";
import { Paiement } from "Paiement/Paiement";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, useHistory } from "react-router-dom";
import { ReferentielLoader } from "Referentiel/Components/ReferentielLoader";
import { POST } from "Request/Components/RequestUtils";
import { GET_LIST_WITH_PARAMS, useJsonRequest } from "Request/Components/useJsonRequest";
import { selectDataResourceReceived } from "Request/Store/selectors";
import { version } from "../../package.json";
import "./App.css";
import { clearToken, clearTokenAdmin, setTokenAdmin, setVersion } from "./Authentification/Store/actions";
import { INITIAL_STATE } from "./Authentification/Store/constants";
import { selectTokenAdminDate, selectTokenAdminValue, selectTokenDate, selectVersionApp } from "./Authentification/Store/selectors";
import { ACCOUNT_MODE, DELAY_REQUEST, DELAY_VERIFICATION, ON_PRIMARY_COLOR, ON_SECONDARY_COLOR, PRIMARY_COLOR, SECONDARY_COLOR } from "./Constants";
import { ResetStore } from "./ResetStore";

/**
 * Point d'entrée de l'application
 * @private
 */
function App() {
  // #region INITIALISATION
  // Initialisation du state
  const [checkToken, setCheckToken] = useState(true);

  // Initialisation des selecteurs
  const actualVersion = useSelector((state) => selectVersionApp(state));
  const tokenAdminValue = useSelector((state) => selectTokenAdminValue(state));
  const tokenDate = useSelector((state) => selectTokenDate(state));
  const tokenDateAdmin = useSelector((state) => selectTokenAdminDate(state));
  const dataReceivedToken = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_AUTHENTIFICATION)); // Retour de la requête de connexion

  // Initialisation des références
  const timerId = useRef(-2);

  // Récupération de l'historique
  const history = useHistory();

  // Initialisation du dispatch
  const dispatch = useDispatch();

  // Traduction i18n
  const { t } = useTranslation();
  // #endregion

  // #region REQUETES
  // Récupération du token admin
  const requestGetTokenAdmin = useJsonRequest({
    command: POST,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_AUTHENTIFICATION,
    tokenObligatory: false,
    silent: true,
    url: URL
  });
  // #endregion

  // #region UTILS
  // Arrêt du timer
  const stopTimer = () => {
    if (timerId.current >= 0) {
      clearTimeout(timerId.current);
    };
  };
  // #endregion

  // #region HOOK D'EFFET
  // Nettoyage du cache si nouvelle version
  useEffect(() => {
    // si pas de version dans le store, on enregistre la version actuelle
    if (!actualVersion) {
      dispatch(setVersion(version));
      return;
    }

    // Si nouvelle version plus haute, on clear le localStorage
    if (version > actualVersion) {
      localStorage.clear();
      window.location.reload();
    }
  }, [actualVersion, dispatch]);

  // Récupération et gestion du token ISIPAY
  useEffect(() => {
    // On sort si le token est toujours actif
    if (tokenAdminValue) {
      return;
    }

    // Requête au serveur du token
    let params = {
      username: loginIsipay.trim(),
      password: mdpIsipay,
    };

    requestGetTokenAdmin(params);
  }, [tokenAdminValue, requestGetTokenAdmin]);

  // Enregistrement du token et navigation
  useEffect(() => {
    if (!dataReceivedToken) {
      return;
    }

    // Enregistrement du token admin
    dispatch(setTokenAdmin(dataReceivedToken.token));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dataReceivedToken]);

  // Détection de nouvelles données reçues
  useEffect(() => {
    if (!checkToken) {
      stopTimer();
      return;
    }

    timerId.current = -1;
    let date = tokenDate ? moment(tokenDate) : null;
    let dateAdmin = tokenDateAdmin ? moment(tokenDateAdmin) : null;

    // Si pas de token ou que la différence entre la date de récupération et la date du moment > 50 minutes, on lance a requête tout de suite
    if (
      (tokenDate && moment().diff(date, "minutes") > DELAY_REQUEST)
      ||
      (tokenDateAdmin && moment().diff(dateAdmin, "minutes") > DELAY_REQUEST)
    ) {
      dispatch(clearToken(INITIAL_STATE.token));
      dispatch(clearTokenAdmin(INITIAL_STATE.tokenAdmin));
      // Navigation et message d'erreur
      history.push({ pathname: "/" });
      // showError(t("accueil.disconnected"));
      setCheckToken(false);
    }

    // On relance la vérification dans 5 minutes
    stopTimer();
    timerId.current = setTimeout(() => {
      setCheckToken(false);
      setCheckToken(true);
    }, DELAY_VERIFICATION);

  }, [checkToken, dispatch, history, t, tokenDate, tokenDateAdmin]);
  // #endregion

  // #region INTERFACE
  return (
    <div className="App-Container">
      <ThemeProvider
        options={{
          primary: PRIMARY_COLOR,
          primaryBg: PRIMARY_COLOR,
          secondary: SECONDARY_COLOR,
          onPrimary: ON_PRIMARY_COLOR,
          onSecondary: ON_SECONDARY_COLOR
        }}
        style={{ height: "100%" }}
      >
        <Header />
        <Switch>
          <Route path="/paiement">
            <Paiement />
          </Route>
          <Route path="/achats">
            <HistoAchats />
          </Route>
          <Route path="/achat">
            <Achat />
          </Route>
          <Route path="/nouveauCompte">
            <Account mode={ACCOUNT_MODE.creation} />
          </Route>
          <Route path="/connexion">
            <Login />
          </Route>
          <Route path="/user">
            <Account mode={ACCOUNT_MODE.modification} />
          </Route>
          <Route path="/">
            {tokenAdminValue ?
              <Accueil />
              :
              <Loading />
            }
          </Route>
        </Switch>
        <SnackbarQueue messages={Queue.messages} timeout={-1} />
        <Loading />
        <ReferentielLoader />
      </ThemeProvider>
      <ErrorOverlay />
      <ResetStore />
    </div>
  );
  // #endregion
}

export default App;
